import React, { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import ReCAPTCHA from 'react-google-recaptcha';
import IntroVideo from '../videos/Axis_background.mp4';
import VideoComponent from './VideoComponent.js';
import PageSEO from "../components/seo/PageSEO.js";
import { useScrollBehavior } from './useScrollBehavior.js';
import "../styles/home.css";
import "../styles/ethics.css";

const SUBMIT_EMAIL  = "YOUR_AXIS_EMAIL@domain.com";
const RECAPTCHA_KEY = "6LdwstIpAAAAAL1_lBneLQEQRHpyUpxdO5ZXJ0R9";

const QUESTIONS = [
  {
    id: "q1",
    text: "Have you read the Axis Codex?",
    options: [
      { id: "a", text: "Not yet — I have been introduced to the principles but haven't read it fully." },
      { id: "b", text: "I have read parts of it." },
      { id: "c", text: "Yes — I have read it." },
      { id: "d", text: "Yes — I have read it carefully and returned to it more than once." },
    ]
  },
  {
    id: "q2",
    text: "Which best describes your current understanding of the Sacred Three?",
    options: [
      { id: "a", text: "I am still learning what they are." },
      { id: "b", text: "I understand them generally." },
      { id: "c", text: "I understand them and agree with them as a personal ethical framework." },
      { id: "d", text: "I have been actively applying them to my decisions for some time." },
    ]
  },
  {
    id: "q3",
    text: "How would you describe your current physical fitness level?",
    options: [
      { id: "a", text: "Sedentary — little to no regular physical activity." },
      { id: "b", text: "Beginner — some activity but no consistent program." },
      { id: "c", text: "Intermediate — consistent training 3–4 times per week." },
      { id: "d", text: "Advanced — high-frequency structured training is a regular part of my life." },
    ]
  },
    {
    id: "q4",
    text: "When you begin something new that is physically or mentally demanding, how do you typically respond once the initial motivation wears off?",
    options: [
      { id: "a", text: "I tend to lose momentum without external accountability." },
      { id: "b", text: "I push through but inconsistently — good stretches followed by gaps." },
      { id: "c", text: "I maintain the habit reasonably well once it is established." },
      { id: "d", text: "Consistency is not usually a problem for me once I have committed." },
    ]
  },
  {
    id: "q5",
    text: "Why do you believe physical training matters for someone committed to the Axis principles — not just for personal health?",
    options: [
      { id: "a", text: "It doesn't specifically — physical fitness is a personal choice." },
      { id: "b", text: "The group requires it so members should comply." },
      { id: "c", text: "A person who neglects their physical capability has decided, in some part of themselves, that they are not serious about acting on what they believe." },
      { id: "d", text: "Physical fitness improves mood and mental clarity which helps with everything." },
    ]
  },
  {
    id: "q6",
    text: "Do you have experience in martial arts or hand to hand combatives?",
    options: [
      { id: "a", text: "None." },
      { id: "b", text: "Some — a few classes or casual exposure." },
      { id: "c", text: "Moderate — consistent training in one or more disciplines." },
      { id: "d", text: "Significant — years of training, competition, or instructing." },
    ]
  },
  {
    id: "q7",
    text: "If you have martial arts or combatives experience, which best describes it?",
    options: [
      { id: "a", text: "No experience." },
      { id: "b", text: "Traditional — Karate, Judo, Taekwondo, or similar." },
      { id: "c", text: "Sport-oriented — BJJ, wrestling, boxing, MMA, or similar." },
      { id: "d", text: "Applied or military combatives — Krav Maga, MCMAP, or similar systems." },
    ]
  },
  {
    id: "q8",
    text: "Do you have military or law enforcement service?",
    options: [
      { id: "a", text: "None." },
      { id: "b", text: "Reserve or National Guard service." },
      { id: "c", text: "Veteran — completed a full term of active duty." },
      { id: "d", text: "Combat or special operations background." },
    ]
  },
  {
    id: "q9",
    text: "You witness someone harming an animal needlessly in a public space. What do you do?",
    options: [
      { id: "a", text: "Nothing — it's not my situation to involve myself in." },
      { id: "b", text: "Observe but don't intervene directly." },
      { id: "c", text: "Intervene verbally, document it, and report to appropriate authorities." },
      { id: "d", text: "Intervene physically without hesitation regardless of circumstances." },
    ]
  },
    {
    id: "q10",
    text: "Someone in a public space is being verbally harassed. There is no immediate physical danger. What is your instinct?",
    options: [
      { id: "a", text: "Stay out of it — it is not my situation." },
      { id: "b", text: "Observe and be ready to act if it escalates." },
      { id: "c", text: "Intervene calmly and verbally to de-escalate." },
      { id: "d", text: "Step in immediately and directly regardless of the specifics." },
    ]
  },
  {
    id: "q11",
    text: "You are new to a group and the training methods are different from what you expected. What is your approach?",
    options: [
      { id: "a", text: "Push back on methods you disagree with early — it sets the right tone." },
      { id: "b", text: "Quietly do things your own way where possible." },
      { id: "c", text: "Engage with the methods as presented, ask questions through the right channel, and reserve judgment until you have real experience with them." },
      { id: "d", text: "Wait to see if others share your concerns before saying anything." },
    ]
  },
    {
    id: "q12",
    text: "You witness someone being physically assaulted in a public space. You are alone and unarmed. What is your instinct?",
    options: [
      { id: "a", text: "Call for help and document it — direct intervention is not my place without training or backup." },
      { id: "b", text: "Assess the situation quickly and intervene verbally to interrupt the attack." },
      { id: "c", text: "Intervene physically if necessary to stop it — someone is being harmed and that is enough." },
      { id: "d", text: "Stay back — getting involved without knowing the full situation could make it worse." },
    ]
  },
  {
    id: "q13",
    text: "How do you handle a group decision you personally disagree with?",
    options: [
      { id: "a", text: "Comply silently and move on." },
      { id: "b", text: "Refuse to participate until the decision is changed." },
      { id: "c", text: "Raise your objection through the appropriate channel, then accept the group's decision once it has been heard." },
      { id: "d", text: "Build consensus among others who agree before raising it formally." },
    ]
  },
  {
    id: "q14",
    text: "The Social Contract — the bonds and mutual obligations between people — is one of the Sacred Three. In your daily life, what does honoring it look like to you?",
    options: [
      { id: "a", text: "Being polite and respectful to people I interact with." },
      { id: "b", text: "Following the laws and social norms of my community." },
      { id: "c", text: "Taking my obligations to others seriously even when it is inconvenient — not just when it is easy." },
      { id: "d", text: "Supporting my immediate family and close friends." },
    ]
  },
  {
    id: "q15",
    text: "What does it mean to you to be part of a group that holds shared ethical principles — not just shared interests?",
    options: [
      { id: "a", text: "It means everyone is on the same page about the rules." },
      { id: "b", text: "It means I am accountable not just for my performance but for whether my behavior reflects those principles — even outside the group." },
      { id: "c", text: "It means I have people to train with who take it seriously." },
      { id: "d", text: "It means the group will hold together under pressure better than one built on casual interest." },
    ]
  },
  {
    id: "q16",
    text: "How do you currently manage physical recovery — sleep, nutrition, and stress?",
    options: [
      { id: "a", text: "Inconsistently — these are areas I know need significant work." },
      { id: "b", text: "Somewhat — I manage some of these but not all." },
      { id: "c", text: "Consistently — I maintain deliberate practices around recovery." },
      { id: "d", text: "Systematically — recovery is a structured part of my training program." },
    ]
  },
  {
    id: "q17",
    text: "The Axis holds that Life — all of it, not just human life — is sacred. In practical terms, what does that mean for how you make decisions?",
    options: [
      { id: "a", text: "It means I try not to cause unnecessary harm to people." },
      { id: "b", text: "It means I consider the effect of my choices on living things beyond my immediate circle — including animals and the natural world." },
      { id: "c", text: "It is a principle I agree with philosophically but haven't thought through in practical terms yet." },
      { id: "d", text: "It means I actively oppose systems and behaviors that treat life as expendable." },
    ]
  },
    {
    id: "q18",
    text: "How would you describe your general comfort level with being in unfamiliar or uncomfortable situations — new environments, new people, physical challenge?",
    options: [
      { id: "a", text: "I find it genuinely difficult and tend to avoid it where possible." },
      { id: "b", text: "I manage it but it takes real effort to push through the discomfort." },
      { id: "c", text: "I am reasonably comfortable — I adapt without too much difficulty." },
      { id: "d", text: "I actively seek it out. Discomfort is where I tend to learn best." },
    ]
  },
    {
    id: "q19",
    text: "You are with a group of people and someone collapses unexpectedly. No one moves immediately. What do you do?",
    options: [
      { id: "a", text: "Wait to see if someone more qualified steps forward." },
      { id: "b", text: "Call 911 and stay on the line for instructions." },
      { id: "c", text: "Move immediately — call for help and begin basic assessment and first aid." },
      { id: "d", text: "Take charge of the scene, delegate tasks, and manage until emergency services arrive." },
    ]
  },
  {
    id: "q20",
    text: "You are walking alone at night and realize you are being followed. What is your instinct?",
    options: [
      { id: "a", text: "Ignore it and hope it resolves — it is probably nothing." },
      { id: "b", text: "Move toward a populated area, stay aware, and prepare mentally for the possibility that it is real." },
      { id: "c", text: "Stop and confront the person directly." },
      { id: "d", text: "Run immediately." },
    ]
  },
  {
    id: "q21",
    text: "You are present when a significantly larger individual begins aggressively threatening a smaller person nearby. The situation has not turned physical yet. What do you do?",
    options: [
      { id: "a", text: "Stay back — physical size disparity makes intervention too dangerous." },
      { id: "b", text: "Call for help and observe." },
      { id: "c", text: "Position yourself nearby, make your presence known, and intervene verbally to interrupt the dynamic." },
      { id: "d", text: "Step between them immediately regardless of the size difference." },
    ]
  },
  {
    id: "q22",
    text: "You witness what appears to be an environmental violation — someone dumping waste into a waterway or burning prohibited materials near a residential area. What is your response?",
    options: [
      { id: "a", text: "It is not your problem — report it only if it directly affects you." },
      { id: "b", text: "Note it but take no action." },
      { id: "c", text: "Document it and report it to the appropriate authority." },
      { id: "d", text: "Confront the person directly and demand they stop." },
    ]
  },
  {
    id: "q23",
    text: "You are part of a small group completing a physically demanding task together. One member is falling behind and slowing the group down. What do you do?",
    options: [
      { id: "a", text: "Push ahead and let the group sort it out." },
      { id: "b", text: "Slow your own pace to stay with the struggling member." },
      { id: "c", text: "Encourage them verbally and adjust the group's approach to bring them along without abandoning the objective." },
      { id: "d", text: "Suggest to the group leader that the struggling member sit out." },
    ]
  },
  {
    id: "q24",
    text: "You find yourself in a situation that has escalated beyond what you are trained or prepared to handle. What governs your decision about whether to act or withdraw?",
    options: [
      { id: "a", text: "Withdraw — acting beyond your capability creates more risk than it resolves." },
      { id: "b", text: "Act anyway — hesitation in a crisis costs more than imperfect action." },
      { id: "c", text: "Do what you can within your actual capability, support those more capable, and don't pretend to a capacity you don't have." },
      { id: "d", text: "Follow the lead of whoever appears most experienced and capable." },
    ]
  },
];

export default function Tier3Assessment() {
  useScrollBehavior();
  const navigate = useNavigate();
  const recaptchaRef = useRef(null);
  const [startTime]  = useState(Date.now());

  const [info, setInfo]                     = useState({ firstName: '', lastName: '', email: '' });
  const [answers, setAnswers]               = useState({});
  const [recaptchaToken, setRecaptchaToken] = useState('');
  const [isSubmitting, setIsSubmitting]     = useState(false);
  const [error, setError]                   = useState('');
  const [showSuccess, setShowSuccess]       = useState(false);

  function handleInfoChange(e) {
    const { name, value } = e.target;
    setInfo(prev => ({ ...prev, [name]: value }));
  }

  function handleAnswer(questionId, optionId) {
    setAnswers(prev => ({ ...prev, [questionId]: optionId }));
  }

  const answered    = Object.keys(answers).length;
  const total       = QUESTIONS.length;
  const allAnswered = answered === total;
  const progress    = Math.round((answered / total) * 100);

  async function handleSubmit(e) {
    e.preventDefault();
    if (!info.firstName || !info.lastName || !info.email) {
      setError('Please complete your name and email before submitting.');
      return;
    }
    if (!allAnswered) {
      setError('Please answer all questions before submitting.');
      return;
    }
    if (!recaptchaToken) {
      setError('Please complete the reCAPTCHA.');
      return;
    }
    if (Date.now() - startTime < 30000) {
      setError('Please take your time with the assessment.');
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      const answerSummary = QUESTIONS.map((q, idx) => {
        const selected = q.options.find(o => o.id === answers[q.id]);
        return `Q${idx + 1}: ${q.text}\nAnswer: ${selected?.text || 'No answer'}`;
      }).join('\n\n');

      const submission = new FormData();
      submission.append('firstName', info.firstName);
      submission.append('lastName',  info.lastName);
      submission.append('email',     info.email);
      submission.append('answers',   answerSummary);
      submission.append('_subject',  `Axis Tier 3 Assessment — ${info.firstName} ${info.lastName}`);
      submission.append('_template', 'table');
      submission.append('_captcha',  'false');
      submission.append('_replyto',  info.email);
      submission.append('_honey',    '');

      const response = await fetch(
        `https://formsubmit.co/ajax/${SUBMIT_EMAIL}`,
        { method: 'POST', headers: { Accept: 'application/json' }, body: submission }
      );
      const result = await response.json();

      if (result.success === 'true' || response.ok) {
        setShowSuccess(true);
      } else {
        setError('Something went wrong. Please try again.');
      }
    } catch {
      setError('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <main className="home">
      <PageSEO
        title="Practitioner Assessment | The Axis"
        description="Axis Practitioner assessment."
        path="/tier-3-assessment"
        jsonLd={{ "@context": "https://schema.org", "@type": "WebPage", "name": "Axis Practitioner Assessment" }}
      />
      <div className="home__bg" aria-hidden="true">
        <VideoComponent videoSrc={IntroVideo} />
      </div>

      <section className="home__content">
        <div className="module et__module">

          <div className="et__header">
            <span className="about__eyebrow">Axis — Tier 3</span>
            <h1 className="home__title">Practitioner Assessment</h1>
            <p className="et__intro">
              This assessment covers your current physical baseline, background,
              ethical orientation, and commitment. There are no
              trick questions. Please answer honestly.
            </p>
            <p className="et__intro">
              Select the response that most accurately reflects how you would
              actually think and act, not what you believe is expected.
            </p>
          </div>

          <form className="et__form" onSubmit={handleSubmit} noValidate>

            <div className="et__section">
              <h2 className="et__section-heading">Your Details</h2>
              <div className="et__info-grid">
                <div className="et__field">
                  <label className="et__label" htmlFor="et-first">First Name</label>
                  <input id="et-first" className="et__input" type="text" name="firstName"
                    value={info.firstName} onChange={handleInfoChange} autoComplete="given-name" required />
                </div>
                <div className="et__field">
                  <label className="et__label" htmlFor="et-last">Last Name</label>
                  <input id="et-last" className="et__input" type="text" name="lastName"
                    value={info.lastName} onChange={handleInfoChange} autoComplete="family-name" required />
                </div>
                <div className="et__field et__field--full">
                  <label className="et__label" htmlFor="et-email">Email</label>
                  <input id="et-email" className="et__input" type="email" name="email"
                    value={info.email} onChange={handleInfoChange} autoComplete="email" required />
                </div>
              </div>
            </div>

            <div className="et__progress-wrap">
              <div className="et__progress-bar">
                <div className="et__progress-fill" style={{ width: `${progress}%` }} />
              </div>
              <span className="et__progress-label">{answered} of {total} answered</span>
            </div>

            <div className="et__questions">
              {QUESTIONS.map((q, idx) => (
                <div key={q.id} className={`et__question ${answers[q.id] ? 'et__question--answered' : ''}`} id={q.id}>
                  <div className="et__question-header">
                    <span className="et__question-number">{String(idx + 1).padStart(2, '0')}</span>
                    <p className="et__question-text">{q.text}</p>
                  </div>
                  <div className="et__options">
                    {q.options.map(opt => (
                      <label key={opt.id} className={`et__option ${answers[q.id] === opt.id ? 'et__option--selected' : ''}`}>
                        <input type="radio" name={q.id} value={opt.id}
                          checked={answers[q.id] === opt.id}
                          onChange={() => handleAnswer(q.id, opt.id)}
                          className="et__radio" />
                        <span className="et__option-marker">{opt.id.toUpperCase()}</span>
                        <span className="et__option-text">{opt.text}</span>
                      </label>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            <div className="et__submit-section">
              {error && <p className="et__error">{error}</p>}
              <div className="et__captcha">
                <ReCAPTCHA ref={recaptchaRef} sitekey={RECAPTCHA_KEY}
                  onChange={token => setRecaptchaToken(token || '')} theme="dark" />
              </div>
              <button className="btn btn--primary et__submit-btn" type="submit" disabled={isSubmitting}>
                {isSubmitting ? "Submitting…" : "Submit Assessment"}
              </button>
              <p className="et__fine-print">
                Your responses are submitted directly to Axis leadership for review.
                You will be contacted at the email address provided.
              </p>
            </div>

          </form>
        </div>
      </section>

      {showSuccess && (
        <div className="et__success-overlay">
          <div className="et__success-box">
            <div className="et__success-icon" aria-hidden="true">
              <svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="24" cy="24" r="20" stroke="currentColor" strokeWidth="2"/>
                <path d="M15 24l7 7 11-12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <span className="jm__eyebrow">Assessment Received</span>
            <h2 className="et__success-title">Submitted</h2>
            <p className="et__success-body">
              Your assessment has been received. Axis leadership will review your
              responses and be in touch at the email address you provided.
            </p>
            <p className="et__success-body">
              The work begins now — not when the paperwork is finalized.
            </p>
            <p className="et__success-mantra">
              Reverence is the why. Power is the can. Duty is the must.
            </p>
            <button className="btn btn--primary" onClick={() => navigate('/')}>
              Return Home
            </button>
          </div>
        </div>
      )}
    </main>
  );
}